import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, {useState} from 'react';
import {Stack, TextField} from '@mui/material';
import {
  Modal,
  Button,
  Headline,
  Body,
  designSystemToken,
} from '@lightricks/react-design-system';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import extractBackendErrorDetail from '@/utils/extractBackendErrorDetail';
import raiseFlashMessage, {
  raiseFlashMessageError,
} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import FlashMessage from '@/components/flash-message';
import useBatchUpdateContractDueDate from '@/hooks/mutations/use-batch-update-contract-due-date';
import useUpdateContractDueDate from '@/hooks/mutations/use-update-contract-due-date';

dayjs.extend(utc);

type ChangeDueDateModalProps = {
  open: boolean;
  onClose: () => void;
  contractId?: string;
  batchContractIds?: string[];
  currentDueDate?: string | null;
  creatorName?: string;
  onSuccess?: () => void;
};

function ChangeDueDateModal({
  open,
  onClose,
  contractId,
  batchContractIds,
  currentDueDate,
  creatorName,
  onSuccess,
}: ChangeDueDateModalProps) {
  const updateDueDate = useUpdateContractDueDate();
  const batchUpdateDueDate = useBatchUpdateContractDueDate();

  const isBatch = batchContractIds !== undefined;
  const activeMutation = isBatch ? batchUpdateDueDate : updateDueDate;
  const count = batchContractIds?.length ?? 0;
  const successSubtitle = isBatch
    ? translate('change-due-date-creators-count', {count})
    : (creatorName ?? '');

  const todayIso = dayjs().format('YYYY-MM-DD');

  const [selectedDate, setSelectedDate] = useState<string>(
    // Batch mode covers many creators that may have different due dates, so
    // there is no single value to pre-fill — default to today. Single mode
    // pre-fills the creator's existing due date.
    !isBatch && currentDueDate
      ? dayjs.utc(currentDueDate).format('YYYY-MM-DD')
      : todayIso
  );
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (activeMutation.isLoading) return;
    setError(null);
    onClose();
  };

  const handleSubmit = () => {
    if (!selectedDate) {
      setError(translate('Please pick a valid date'));
      return;
    }
    if (selectedDate < todayIso) {
      setError(translate('Due date cannot be in the past'));
      return;
    }

    // Guard against dispatching an empty/invalid request (no batch ids, or a
    // missing single contract id).
    const hasTarget = isBatch ? count > 0 : Boolean(contractId);
    if (!hasTarget) {
      return;
    }

    AnalyticsService.dispatchEvent('participants-due-date-selected', {
      event_type: 'click',
      category: 'Participants - Due Date',
      label: 'select date',
    });

    // Send UTC midnight so the backend stores the picked calendar day
    // regardless of the user's local timezone.
    const dueAt = `${selectedDate}T00:00:00Z`;
    const mutationCallbacks = {
      onSuccess: () => {
        raiseFlashMessage({
          status: 'success',
          mode: 'light',
          message: (
            <FlashMessage.Banner
              title={translate('Due date updated')}
              subtitle={successSubtitle}
            />
          ),
        });
        onSuccess?.();
        onClose();
      },
      onError: (err: unknown) => {
        raiseFlashMessageError({
          title: translate('Failed to update due date'),
          subtitle: extractBackendErrorDetail(err),
        });
      },
    };

    if (isBatch) {
      batchUpdateDueDate.mutate(
        {contractIds: batchContractIds ?? [], dueAt},
        mutationCallbacks
      );
    } else {
      updateDueDate.mutate(
        {contractId: contractId ?? '', dueAt},
        mutationCallbacks
      );
    }
  };

  return (
    <Modal open={open} handleClose={handleClose} size="small" showCloseButton>
      <Stack gap="16px" padding="24px" alignItems="flex-start">
        <Headline size="md">{translate('Change due date')}</Headline>
        <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
          {isBatch
            ? translate('change-due-date-batch-instructions', {count})
            : translate('Pick a new date for content delivery.')}
        </Body>
        <TextField
          fullWidth
          type="date"
          value={selectedDate}
          onChange={(e) => {
            setSelectedDate(e.target.value);
            setError(null);
          }}
          inputProps={{min: todayIso}}
          error={!!error}
          helperText={error ?? ''}
        />
        <Stack
          direction="row"
          justifyContent="flex-end"
          gap="12px"
          sx={{width: '100%'}}
        >
          <Button
            mode="tinted"
            appearance="neutral"
            size="medium"
            onClick={handleClose}
            disabled={activeMutation.isLoading}
          >
            {translate('Cancel')}
          </Button>
          <Button
            mode="filled"
            appearance="neutral"
            size="medium"
            onClick={handleSubmit}
            disabled={activeMutation.isLoading || !selectedDate}
          >
            {translate('Save')}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}

export default ChangeDueDateModal;
